// qs-ColorPicker.vue
<template>
  <div
    class="color-picker"
    @click.stop
  >
    <div class="color-picker-content">
      <div
        v-if="showPreview"
        class="color-picker-large-preview"
      >
        <div
          class="color-picker-large-preview-color"
          :style="{
            backgroundColor: currentColorOpaque,
            opacity: opacity / 100
          }"
        />
      </div>

      <div class="color-picker-main">
        <div
          class="color-picker-saturation"
          :style="{ backgroundColor: `hsl(${hue}, 100%, 50%)` }"
          @mousedown="startSaturationDrag"
          @touchstart.passive="startSaturationDrag"
        >
          <div class="color-picker-white-overlay" />
          <div class="color-picker-black-overlay" />
          <div
            class="color-picker-handle"
            :style="{
              left: `${saturation * 100}%`,
              top: `${(1 - lightness) * 100}%`
            }"
          />
        </div>

        <div class="color-picker-hue-container">
          <div
            class="color-picker-hue"
            @mousedown="startHueDrag"
            @touchstart.passive="startHueDrag"
          >
            <div
              class="color-picker-hue-handle"
              :style="{ left: `${(hue / 360) * 100}%` }"
            />
          </div>
        </div>
      </div>

      <div
        v-if="showOpacity"
        class="color-picker-opacity"
      >
        <div class="color-picker-opacity-container">
          <div class="color-picker-opacity-track">
            <div
              class="color-picker-opacity-gradient"
              :style="{
                background: `linear-gradient(90deg, transparent, ${currentColorOpaque})`
              }"
            />
            <div
              class="color-picker-opacity-handle"
              :style="{ left: `${opacity}%` }"
            />
          </div>
          <input
            type="range"
            min="0"
            max="100"
            :value="opacity"
            class="color-picker-opacity-slider"
            @input="updateOpacity"
            @click.stop
          >
        </div>
        <span class="color-picker-opacity-value">{{ opacity }}%</span>
      </div>

      <div
        v-if="showInputs"
        class="color-picker-inputs"
      >
        <div class="color-picker-input-group">
          <label class="color-picker-label">#</label>
          <input
            v-model="hexInput"
            type="text"
            class="color-picker-input"
            placeholder="000000"
            maxlength="6"
            @input="updateFromHex"
            @click.stop
            @focus="cursorManager.setTextCursor()"
            @blur="cursorManager.resetToDrawing()"
          >
        </div>

        <div class="color-picker-input-group">
          <label class="color-picker-label">{{ showOpacity ? 'RGBA' : 'RGB' }}</label>
          <div class="color-picker-rgba-inputs">
            <input
              v-model="rInput"
              type="number"
              class="color-picker-input color-picker-input-small"
              placeholder="R"
              min="0"
              max="255"
              @input="finalUpdateFromRgba('r')"
              @click.stop
              @focus="cursorManager.setTextCursor()"
              @blur="cursorManager.resetToDrawing()"
            >
            <input
              v-model="gInput"
              type="number"
              class="color-picker-input color-picker-input-small"
              placeholder="G"
              min="0"
              max="255"
              @input="finalUpdateFromRgba('g')"
              @click.stop
              @focus="cursorManager.setTextCursor()"
              @blur="cursorManager.resetToDrawing()"
            >
            <input
              v-model="bInput"
              type="number"
              class="color-picker-input color-picker-input-small"
              placeholder="B"
              min="0"
              max="255"
              @input="finalUpdateFromRgba('b')"
              @click.stop
              @focus="cursorManager.setTextCursor()"
              @blur="cursorManager.resetToDrawing()"
            >
            <input
              v-if="showOpacity"
              v-model="aInput"
              type="number"
              class="color-picker-input color-picker-input-small"
              placeholder="A"
              min="0"
              max="100"
              @input="finalUpdateFromRgba('a')"
              @click.stop
              @focus="cursorManager.setTextCursor()"
              @blur="cursorManager.resetToDrawing()"
            >
          </div>
        </div>

        <div class="color-picker-input-group">
          <label class="color-picker-label">CMYK</label>
          <div class="color-picker-cmyk-inputs">
            <input
              v-model="cInput"
              type="number"
              class="color-picker-input color-picker-input-small"
              placeholder="C"
              min="0"
              max="100"
              @input="updateFromCmyk('c')"
              @click.stop
              @focus="cursorManager.setTextCursor()"
              @blur="cursorManager.resetToDrawing()"
            >
            <input
              v-model="mInput"
              type="number"
              class="color-picker-input color-picker-input-small"
              placeholder="M"
              min="0"
              max="100"
              @input="updateFromCmyk('m')"
              @click.stop
              @focus="cursorManager.setTextCursor()"
              @blur="cursorManager.resetToDrawing()"
            >
            <input
              v-model="yInput"
              type="number"
              class="color-picker-input color-picker-input-small"
              placeholder="Y"
              min="0"
              max="100"
              @input="updateFromCmyk('y')"
              @click.stop
              @focus="cursorManager.setTextCursor()"
              @blur="cursorManager.resetToDrawing()"
            >
            <input
              v-model="kInput"
              type="number"
              class="color-picker-input color-picker-input-small"
              placeholder="K"
              min="0"
              max="100"
              @input="updateFromCmyk('k')"
              @click.stop
              @focus="cursorManager.setTextCursor()"
              @blur="cursorManager.resetToDrawing()"
            >
          </div>
        </div>
      </div>

      <button
        v-if="showApplyButton"
        class="color-picker-apply-button"
        @click="applyColor"
        @click.stop
      >
        Apply
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useColorDrag } from '@/composables/qs-useColorDrag'
import { useColorInputs } from '@/composables/qs-useColorInputs'
import { hexToRgb } from '@/utils/qs-util-colorUtils'
import { cursorManager } from '@/utils/qs-cursorManager'

interface Props {
  modelValue: string
  opacity?: number
  showOpacity?: boolean
  showInputs?: boolean
  showApplyButton?: boolean
  showPreview?: boolean
  isolateMode?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  opacity: 100,
  showOpacity: true,
  showInputs: true,
  showApplyButton: true,
  showPreview: true,
  isolateMode: false
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
  'update:opacity': [value: number]
  'apply': []
  'change': [color: string, opacity: number]
}>()

const red = ref(0)
const green = ref(0)
const blue = ref(0)
const opacity = ref(props.opacity)
const hue = ref(0)
const saturation = ref(0)
const lightness = ref(0)

const {
  hexInput,
  rInput,
  gInput,
  bInput,
  aInput,
  cInput,
  mInput,
  yInput,
  kInput,
  updateFromHex,
  updateFromRgba,
  updateFromCmyk,
  updateHslFromRgb,
  updateInputFields
} = useColorInputs(red, green, blue, opacity, hue, saturation, lightness, (event, value) => {
  if (event === 'update:modelValue') {
    const color = props.showOpacity
      ? `rgba(${red.value}, ${green.value}, ${blue.value}, ${opacity.value / 100})`
      : value as string
    emit('update:modelValue', color)
  }
  if (event === 'update:opacity') {
    emit('update:opacity', value as number)
  }
})

const {
  startSaturationDrag,
  startHueDrag
} = useColorDrag(red, green, blue, hue, saturation, lightness, (event, value) => {
  if (event === 'update:modelValue') {
    const color = props.showOpacity
      ? `rgba(${red.value}, ${green.value}, ${blue.value}, ${opacity.value / 100})`
      : value as string
    emit('update:modelValue', color)
  } else {
    emit(event, value)
  }
})

const currentColorOpaque = computed(() => {
  return `rgb(${red.value}, ${green.value}, ${blue.value})`
})

const formatColor = (): string => {
  if (props.showOpacity) {
    return `rgba(${red.value}, ${green.value}, ${blue.value}, ${opacity.value / 100})`
  }
  const r = red.value.toString(16).padStart(2, '0')
  const g = green.value.toString(16).padStart(2, '0')
  const b = blue.value.toString(16).padStart(2, '0')
  return `#${r}${g}${b}`
}

const isolatedUpdateFromRgba = (component: 'r' | 'g' | 'b' | 'a'): void => {
  if (component === 'r') {
    red.value = Math.max(0, Math.min(255, parseInt(rInput.value.toString()) || 0))
  } else if (component === 'g') {
    green.value = Math.max(0, Math.min(255, parseInt(gInput.value.toString()) || 0))
  } else if (component === 'b') {
    blue.value = Math.max(0, Math.min(255, parseInt(bInput.value.toString()) || 0))
  } else if (component === 'a') {
    opacity.value = Math.max(0, Math.min(100, parseInt(aInput.value.toString()) || 0))
  }

  if (component !== 'a') {
    updateHslFromRgb()
    updateInputFields()
  }

  emit('update:modelValue', formatColor())
}

const finalUpdateFromRgba = props.isolateMode ? isolatedUpdateFromRgba : updateFromRgba

const updateOpacity = (event: Event): void => {
  const target = event.target as HTMLInputElement
  opacity.value = parseInt(target.value, 10)
  emit('update:opacity', opacity.value)
  emit('update:modelValue', formatColor())
  emit('change', formatColor(), opacity.value)
}

const applyColor = (): void => {
  const color = formatColor()
  emit('update:modelValue', color)
  emit('update:opacity', opacity.value)
  emit('change', color, opacity.value)
  emit('apply')
}

watch(() => props.modelValue, (newValue) => {
  if (!newValue) return

  if (newValue.startsWith('#')) {
    const rgb = hexToRgb(newValue)
    red.value = rgb.r
    green.value = rgb.g
    blue.value = rgb.b
    updateHslFromRgb()
    updateInputFields()
  } else if (newValue.startsWith('rgba(')) {
    const rgbaMatch = newValue.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/)
    if (rgbaMatch) {
      red.value = parseInt(rgbaMatch[1], 10)
      green.value = parseInt(rgbaMatch[2], 10)
      blue.value = parseInt(rgbaMatch[3], 10)
      opacity.value = Math.round(parseFloat(rgbaMatch[4]) * 100)
      updateHslFromRgb()
      updateInputFields()
    }
  } else if (newValue.startsWith('rgb(')) {
    const rgbMatch = newValue.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/)
    if (rgbMatch) {
      red.value = parseInt(rgbMatch[1], 10)
      green.value = parseInt(rgbMatch[2], 10)
      blue.value = parseInt(rgbMatch[3], 10)
      updateHslFromRgb()
      updateInputFields()
    }
  }
}, { immediate: true })

watch(() => props.opacity, (newValue) => {
  opacity.value = newValue
  aInput.value = Math.round(newValue)
})

watch([red, green, blue], () => {
  updateInputFields()
})

updateInputFields()
</script>

<style scoped>
.color-picker {
  background: transparent;
  border: none;
  border-radius: 0;
  width: 100%;
  min-width: 160px;
}

.color-picker-content {
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.color-picker-main {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.color-picker-saturation {
  position: relative;
  width: 100%;
  height: 60px;
  min-height: 50px;
  cursor: crosshair;
  border-top: 2px solid theme('colors.gray.100');
  border-left: none;
  border-right: none;
  border-bottom: none;
  border-radius: 0;
}

.color-picker-white-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to right, rgba(255,255,255,0.8), transparent);
  border-radius: 0;
}

.color-picker-black-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to top, black, transparent);
  border-radius: 0;
}

.color-picker-handle {
  position: absolute;
  width: 12px;
  height: 12px;
  border: 2px solid theme('colors.creme.light');
  border-radius: 0;
  transform: translate(-50%, -50%);
  cursor: crosshair;
  background: transparent;
  touch-action: none;
}

.color-picker-hue-container {
  width: 100%;
  height: 16px;
  min-height: 14px;
  border-top: 2px solid theme('colors.gray.100');
  border-left: none;
  border-right: none;
  border-bottom: none;
  border-radius: 0;
}

.color-picker-hue {
  position: relative;
  width: 100%;
  height: 100%;
  background: linear-gradient(to right,
    #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000
  );
  cursor: pointer;
  border: none;
  border-radius: 0;
}

.color-picker-hue-handle {
  position: absolute;
  top: 0;
  width: 4px;
  height: 100%;
  background: theme('colors.creme.light');
  border: 1px solid theme('colors.gray.300');
  transform: translateX(-50%);
  cursor: pointer;
  touch-action: none;
  border-radius: 0;
}

.color-picker-opacity {
  display: flex;
  align-items: center;
  gap: 0;
  background: theme('colors.creme.light'); 
  padding: 2px; 
  border: none; 
  border-radius: 0;
}

.color-picker-label {
  font-size: 10px;
  font-weight: 400;
  color: theme('colors.text.DEFAULT'); 
  width: 35px;
  flex-shrink: 0;
  text-align: left;
}

.color-picker-opacity-container {
  position: relative;
  flex: 1;
  height: 16px;
  min-height: 14px;
  border-radius: 0;
}

.color-picker-opacity-track {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: theme('colors.creme.light'); 
  border: none;
  overflow: hidden;
  border-radius: 0;
}

.color-picker-opacity-gradient {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 0;
}

.color-picker-opacity-handle {
  position: absolute;
  top: -2px;
  width: 3px;
  height: 20px;
  background: theme('colors.creme.light');
  border: 1px solid theme('colors.gray.300');
  transform: translateX(-50%);
  cursor: pointer;
  touch-action: none;
  border-radius: 0;
}

.color-picker-opacity-slider {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  z-index: 10;
}

.color-picker-opacity-value {
  font-size: 10px;
  color: theme('colors.text.DEFAULT'); 
  width: 30px; 
  flex-shrink: 0; 
  text-align: right;
}

.color-picker-inputs {
  display: flex;
  flex-direction: column;
  gap: 0;
  background: theme('colors.creme.lighter'); 
  padding: 4px;
  border-radius: 0;
}

.color-picker-input-group {
  display: flex;
  align-items: center;
  gap: 0;
}

.color-picker-input {
  flex: 1;
  padding: 2px 4px;
  border: none;
  border-radius: 0;
  font-size: 11px;
  font-family: monospace;
  background: theme('colors.creme.light');
  color: theme('colors.text.DEFAULT'); 
  min-height: 20px;
}

.color-picker-input:focus {
  outline: none;
  background: theme('colors.creme.light');
  color: theme('colors.text.DEFAULT'); 
}

.color-picker-input-small {
  flex: 1;
  width: 30px;
  min-width: 25px;
  padding: 2px;
  text-align: center;
  min-height: 20px;
}

.color-picker-rgba-inputs,
.color-picker-cmyk-inputs {
  display: flex;
  gap: 0;
  flex: 1;
}

.color-picker-rgba-inputs input,
.color-picker-cmyk-inputs input {
  flex: 1;
}

.color-picker-large-preview {
  width: 100%;
  height: 40px;
  min-height: 35px;
  margin-top: 0;
  margin-bottom: 0;
  border-top: none;
  border-bottom: none;
  border-left: none;
  border-right: none;
  background: transparent;
  border-radius: theme('borderRadius.DEFAULT') theme('borderRadius.DEFAULT') 0 0; 
}

.color-picker-large-preview-color {
  width: 100%;
  height: 100%;
  border-radius: theme('borderRadius.DEFAULT') theme('borderRadius.DEFAULT') 0 0; 
}


.color-picker-apply-button {
  width: 100%;
  height: 36px;
  padding: 0 12px;
  background-color: theme('colors.primary.500');
  color: theme('colors.creme.light');
  border: none;
  border-radius: 0 0 theme('borderRadius.DEFAULT') theme('borderRadius.DEFAULT') !important; 
  cursor: pointer;
  transition: background-color 0.2s ease;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 12px;
  font-weight: 500;
  outline: none;
}

.color-picker-apply-button:hover {
  background-color: theme('colors.primary.700');
}

.color-picker-apply-button:active {
  background-color: theme('colors.primary.600');
}
</style>